// src/components/dashboard/ContactWorkspace.tsx
//
// Slide-over "follow-up workspace" for a single contact. Shows a unified
// activity timeline and lets an admin: log a note, log a call, schedule a
// task/reminder, send an email, and change the lead's lifecycle status.
import React, { useState } from "react";
import {
  X,
  Loader2,
  StickyNote,
  Phone,
  CalendarClock,
  Mail,
  CheckCircle2,
  Clock,
  CreditCard,
  Activity,
} from "lucide-react";
import {
  useGetContactTimelineQuery,
  useCreateFollowUpMutation,
  useSendContactEmailMutation,
  useUpdateLeadStatusMutation,
  useUpdateFollowUpMutation,
} from "../../app/api/adminApi";

function cn(...c: Array<string | false | null | undefined>) {
  return c.filter(Boolean).join(" ");
}
const fmtDate = (d?: string | null) =>
  d
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(d))
    : "";

const LEAD_STATUSES = [
  "new",
  "contacted",
  "nurturing",
  "lead",
  "customer",
  "churned",
  "archived",
];

type Tab = "timeline" | "note" | "call" | "task" | "email";

const kindIcon = (kind: string, subKind?: string) => {
  if (kind === "order") return <CreditCard className="h-4 w-4 text-sky-500" />;
  if (subKind === "email") return <Mail className="h-4 w-4 text-violet-500" />;
  if (subKind === "call") return <Phone className="h-4 w-4 text-emerald-500" />;
  if (subKind === "task")
    return <CalendarClock className="h-4 w-4 text-amber-500" />;
  if (subKind === "status_change")
    return <Activity className="h-4 w-4 text-indigo-500" />;
  return <StickyNote className="h-4 w-4 text-slate-400" />;
};

const ContactWorkspace: React.FC<{
  email: string;
  name?: string;
  onClose: () => void;
}> = ({ email, name, onClose }) => {
  const [tab, setTab] = useState<Tab>("timeline");
  const [text, setText] = useState("");
  const [channel, setChannel] = useState("phone");
  const [dueAt, setDueAt] = useState("");
  const [subject, setSubject] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isLoading, refetch } = useGetContactTimelineQuery({ email });
  const [createFollowUp, { isLoading: creating }] = useCreateFollowUpMutation();
  const [sendEmail, { isLoading: sending }] = useSendContactEmailMutation();
  const [updateStatus, { isLoading: statusUpdating }] =
    useUpdateLeadStatusMutation();
  const [updateFollowUp] = useUpdateFollowUpMutation();

  const timeline = data?.data?.timeline || [];
  const lead = data?.data?.lead;

  const busy = creating || sending || statusUpdating;

  const resetForms = () => {
    setText("");
    setSubject("");
    setDueAt("");
  };

  const flash = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 2500);
  };

  const submitNote = async (type: "note" | "call" | "task") => {
    if (!text.trim() && type !== "task") return;
    try {
      await createFollowUp({
        contactEmail: email,
        contactName: name,
        type,
        message: text.trim(),
        channel: type === "call" ? channel : undefined,
        dueAt: type === "task" && dueAt ? new Date(dueAt).toISOString() : null,
      }).unwrap();
      resetForms();
      setTab("timeline");
      flash(`${type[0].toUpperCase()}${type.slice(1)} saved`);
    } catch {
      flash("Failed to save");
    }
  };

  const submitEmail = async () => {
    if (!subject.trim() || !text.trim()) return;
    try {
      await sendEmail({
        email,
        subject: subject.trim(),
        message: text.trim(),
        contactName: name,
      }).unwrap();
      resetForms();
      setTab("timeline");
      flash("Email sent");
    } catch {
      flash("Failed to send email");
    }
  };

  const changeStatus = async (status: string) => {
    try {
      await updateStatus({ email, status }).unwrap();
      flash(`Status → ${status}`);
    } catch {
      flash("Status update needs a lead record");
    }
  };

  const toggleTask = async (id: string, completed: boolean) => {
    try {
      await updateFollowUp({ id, completed: !completed }).unwrap();
      refetch();
    } catch {
      /* ignore */
    }
  };

  const tabs: Array<{ key: Tab; label: string; icon: React.ReactNode }> = [
    { key: "timeline", label: "Timeline", icon: <Activity className="h-4 w-4" /> },
    { key: "note", label: "Note", icon: <StickyNote className="h-4 w-4" /> },
    { key: "call", label: "Call", icon: <Phone className="h-4 w-4" /> },
    { key: "task", label: "Task", icon: <CalendarClock className="h-4 w-4" /> },
    { key: "email", label: "Email", icon: <Mail className="h-4 w-4" /> },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-900/30"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-lg flex-col bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-start justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="font-semibold text-slate-900">
              {name || email || "Contact"}
            </h2>
            <p className="text-xs text-slate-400">{email}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* lifecycle status */}
        <div className="border-b border-slate-100 px-5 py-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Lifecycle status {lead ? "" : "(no lead record)"}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {LEAD_STATUSES.map((s) => (
              <button
                key={s}
                disabled={statusUpdating}
                onClick={() => changeStatus(s)}
                className={cn(
                  "rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize transition",
                  lead?.status === s
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 text-slate-500 hover:bg-slate-50"
                )}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* tabs */}
        <div className="flex gap-1 border-b border-slate-100 px-3 py-2">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition",
                tab === t.key
                  ? "bg-slate-900 text-white"
                  : "text-slate-500 hover:bg-slate-50"
              )}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {feedback && (
          <div className="mx-5 mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700">
            {feedback}
          </div>
        )}

        {/* body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {tab === "timeline" && (
            <>
              {isLoading ? (
                <div className="flex justify-center py-10 text-slate-400">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : timeline.length ? (
                <ol className="relative space-y-4 border-l border-slate-200 pl-5">
                  {timeline.map((t) => (
                    <li key={`${t.kind}-${t.id}`} className="relative">
                      <span className="absolute -left-[27px] flex h-5 w-5 items-center justify-center rounded-full bg-white ring-4 ring-white">
                        {kindIcon(t.kind, t.subKind)}
                      </span>
                      <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-slate-800">
                            {t.title}
                          </p>
                          <span className="shrink-0 text-[10px] text-slate-400">
                            {fmtDate(t.at)}
                          </span>
                        </div>
                        {t.message && (
                          <p className="mt-1 whitespace-pre-wrap text-xs text-slate-600">
                            {t.message}
                          </p>
                        )}
                        <div className="mt-1 flex items-center gap-2">
                          {t.by && (
                            <span className="text-[10px] text-slate-400">
                              by {t.by}
                            </span>
                          )}
                          {t.subKind === "task" && (
                            <button
                              onClick={() => toggleTask(t.id, !!t.completed)}
                              className={cn(
                                "flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold",
                                t.completed
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-amber-100 text-amber-700"
                              )}
                            >
                              {t.completed ? (
                                <CheckCircle2 className="h-3 w-3" />
                              ) : (
                                <Clock className="h-3 w-3" />
                              )}
                              {t.completed ? "Done" : "Open"}
                              {t.dueAt ? ` · ${fmtDate(t.dueAt)}` : ""}
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="py-12 text-center text-sm text-slate-400">
                  No activity yet. Log a note or send an email to get started.
                </div>
              )}
            </>
          )}

          {(tab === "note" || tab === "call") && (
            <div className="space-y-3">
              {tab === "call" && (
                <div className="flex gap-1.5">
                  {["phone", "whatsapp", "sms"].map((c) => (
                    <button
                      key={c}
                      onClick={() => setChannel(c)}
                      className={cn(
                        "rounded-lg border px-3 py-1.5 text-xs font-semibold capitalize",
                        channel === c
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 text-slate-500"
                      )}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                placeholder={
                  tab === "call"
                    ? "Summarize the call…"
                    : "Write an internal note…"
                }
                className="w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-violet-300"
              />
              <button
                disabled={busy || !text.trim()}
                onClick={() => submitNote(tab)}
                className="w-full rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy ? "Saving…" : tab === "call" ? "Log call" : "Save note"}
              </button>
            </div>
          )}

          {tab === "task" && (
            <div className="space-y-3">
              <label className="block text-xs font-semibold text-slate-500">
                Reminder date &amp; time
              </label>
              <input
                type="datetime-local"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                className="w-full rounded-xl border border-slate-200 p-2.5 text-sm outline-none focus:border-violet-300"
              />
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                placeholder="What needs to happen? e.g. Follow up on payment"
                className="w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-violet-300"
              />
              <button
                disabled={busy || !dueAt}
                onClick={() => submitNote("task")}
                className="w-full rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy ? "Saving…" : "Schedule follow-up"}
              </button>
            </div>
          )}

          {tab === "email" && (
            <div className="space-y-3">
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject"
                className="w-full rounded-xl border border-slate-200 p-2.5 text-sm outline-none focus:border-violet-300"
              />
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={7}
                placeholder={`Hi ${name || "there"},\n\nWrite your message…`}
                className="w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-violet-300"
              />
              <button
                disabled={sending || !subject.trim() || !text.trim()}
                onClick={submitEmail}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                <Mail className="h-4 w-4" />
                {sending ? "Sending…" : "Send email"}
              </button>
              <p className="text-center text-xs text-slate-400">
                Sends a real email to {email} and logs it to the timeline.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactWorkspace;
